import React, { useCallback } from 'react';
import {
    Modal,
    ModalProps,
    Button,
    ButtonProps,
    Box,
    Stack,
    Flex,
} from '@mantine/core';
import { showErrorNotification } from '../../notifications';

export interface ConfirmModalProps {
    opened: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => void | Promise<void>;
    loading?: boolean;
    confirmColor?: ButtonProps['color'];
    cancelColor?: ButtonProps['color'];
    confirmButtonProps?: Partial<ButtonProps>;
    cancelButtonProps?: Partial<ButtonProps>;
    modalProps?: Partial<ModalProps>;
}

export function ConfirmModal({
    opened,
    onClose,
    title,
    children,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    onConfirm,
    loading = false,
    confirmColor = 'primary',
    cancelColor = 'gray',
    confirmButtonProps,
    cancelButtonProps,
    modalProps,
}: ConfirmModalProps) {
    const handleConfirm = useCallback(async () => {
        try {
            await onConfirm();
            onClose();
        } catch (error: unknown) {
            const message =
                error instanceof Error && error.message.trim()
                    ? error.message
                    : 'Something went wrong. Please try again.';
            showErrorNotification(message);
        }
    }, [onConfirm, onClose]);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={title}
            centered
            size="md"
            radius="md"
            classNames={{
                title: 'text-2xl font-bold',
            }}
            {...modalProps}
        >
            <Box bg="white" className="rounded-xl">
                <Stack gap="lg">
                    {children}
                    <Flex justify="flex-end" gap="sm" mt="md">
                        <Button
                            variant="outline"
                            size="sm"
                            radius="xl"
                            fullWidth={false}
                            color={cancelColor}
                            {...cancelButtonProps}
                            onClick={onClose}
                            disabled={loading}
                        >
                            {cancelLabel}
                        </Button>
                        <Button
                            variant="filled"
                            size="sm"
                            radius="xl"
                            fullWidth={false}
                            color={confirmColor}
                            {...confirmButtonProps}
                            loading={loading}
                            onClick={handleConfirm}
                        >
                            {confirmLabel}
                        </Button>
                    </Flex>
                </Stack>
            </Box>
        </Modal>
    );
}
